import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Copy, Eye, EyeOff, Key } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import { Checkbox } from '../../../../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../../components/ui/dialog';
import { Input } from '../../../../../components/ui/input';
import apiService from '../../../../../services/api.service';
import ActionsEnum from '../../enums/actions.enum';
import EntitiesEnum from '../../enums/entities.enum';

const SCOPES = Object.values(ActionsEnum);
const ENTITIES = Object.values(EntitiesEnum);

function CreateApiKeyDialog({ isOpen, onClose, onApiKeyCreated }) {
  const [keyName, setKeyName] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [selectedScopes, setSelectedScopes] = useState([]);
  const [nameError, setNameError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // State for showing the created key
  const [createdApiKey, setCreatedApiKey] = useState('');
  const [isKeyVisible, setIsKeyVisible] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const validateName = async (name) => {
    if (!name) {
      setNameError('Name is required');
      return false;
    }
    if (!name.match(/^[a-zA-Z0-9-_:]*$/)) {
      setNameError('Only letters, numbers, hyphens, underscores, and colons allowed');
      return false;
    }
    try {
      const existingNames = await apiService.admin.getApiKeysNames();
      if (existingNames.includes(name)) {
        setNameError('Name already exists');
        return false;
      }
    } catch (error) {
      console.error('Error validating name:', error);
    }
    setNameError('');
    return true;
  };

  const handleScopeChange = (entity, scope, checked) => {
    const scopeKey = `${entity}:${scope}`;
    if (checked) {
      setSelectedScopes((prev) => [...prev, scopeKey]);
    } else {
      setSelectedScopes((prev) => prev.filter((s) => s !== scopeKey));
    }
  };

  const handleSave = async () => {
    const isValid = await validateName(keyName);
    if (!isValid) return;

    setIsSaving(true);
    try {
      // Convert selectedScopes array to the required format
      const scopesObject = selectedScopes.reduce((acc, scopeKey) => {
        const [entity, scope] = scopeKey.split(':');
        if (!acc[entity]) {
          acc[entity] = [];
        }
        acc[entity].push(scope);
        return acc;
      }, {});

      const response = await apiService.admin.saveApiKey({
        name: keyName,
        expirationDate: new Date(expirationDate).toISOString(),
        scopes: scopesObject,
      });

      setCreatedApiKey(response);
    } catch (error) {
      console.error('Error creating API key:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCopyKey = async () => {
    try {
      await navigator.clipboard.writeText(createdApiKey);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const handleClose = () => {
    // Reset all state
    setKeyName('');
    setExpirationDate('');
    setSelectedScopes([]);
    setNameError('');
    setCreatedApiKey('');
    setIsKeyVisible(false);
    setIsCopied(false);

    if (createdApiKey) {
      onApiKeyCreated();
    } else {
      onClose();
    }
  };

  const isFormValid = keyName && expirationDate && selectedScopes.length > 0 && !nameError;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            {createdApiKey ? 'API Key Created' : 'Create API Key'}
          </DialogTitle>
          <DialogDescription>
            {createdApiKey
              ? "Your API key has been created. Make sure to copy it now - you won't be able to see it again."
              : 'Enter a name for your API key and select the permissions it should have.'}
          </DialogDescription>
        </DialogHeader>

        {createdApiKey ? (
          <div className="space-y-4">
            <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg space-y-3">
              <div className="flex items-center gap-2">
                <Input
                  type={isKeyVisible ? 'text' : 'password'}
                  value={createdApiKey}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setIsKeyVisible(!isKeyVisible)}
                >
                  {isKeyVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
                <Button type="button" variant="outline" size="icon" onClick={handleCopyKey}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              {isCopied && <p className="text-sm text-green-600">Copied to clipboard!</p>}
            </div>
            <p className="text-sm text-muted-foreground">
              Store this key securely. For security reasons, we cannot show it again.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Key Name */}
            <div className="space-y-2">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="apikey-name" className="text-sm font-medium">
                Key Name
              </label>
              <Input
                id="apikey-name"
                value={keyName}
                onChange={(e) => {
                  setKeyName(e.target.value);
                  if (nameError) validateName(e.target.value);
                }}
                onBlur={() => keyName && validateName(keyName)}
                placeholder="my-api-key"
                className={nameError ? 'border-red-500' : ''}
              />
              {nameError && <p className="text-xs text-red-500">{nameError}</p>}
            </div>

            {/* Expiration Date */}
            <div className="space-y-2">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="apikey-expiration" className="text-sm font-medium">
                Expiration Date
              </label>
              <Input
                id="apikey-expiration"
                type="datetime-local"
                value={expirationDate}
                onChange={(e) => setExpirationDate(e.target.value)}
              />
            </div>

            {/* Scopes */}
            <div className="space-y-2">
              <p className="text-sm font-medium">Scopes</p>
              <p className="text-xs text-muted-foreground mb-3">
                Select the permissions this key should have.
              </p>

              {ENTITIES.map((entity) => (
                <div key={entity} className="mb-4">
                  <p className="text-sm font-medium mb-2 capitalize">{entity}</p>
                  <div className="flex flex-wrap gap-4">
                    {SCOPES.map((scope) => {
                      const scopeKey = `${entity}:${scope}`;
                      const isChecked = selectedScopes.includes(scopeKey);
                      const checkboxId = `scope-${entity}-${scope}`;
                      return (
                        <label
                          key={scopeKey}
                          htmlFor={checkboxId}
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <Checkbox
                            id={checkboxId}
                            checked={isChecked}
                            onCheckedChange={(checked) => handleScopeChange(entity, scope, checked)}
                          />
                          <span className="text-sm capitalize">{scope}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          {createdApiKey ? (
            <Button onClick={handleClose}>Done</Button>
          ) : (
            <>
              <Button variant="outline" onClick={handleClose} disabled={isSaving}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={!isFormValid || isSaving}>
                {isSaving ? 'Creating...' : 'Create Key'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

CreateApiKeyDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onApiKeyCreated: PropTypes.func.isRequired,
};

export default CreateApiKeyDialog;
